/**
 * Skaner kodów kreskowych v6 - html5-qrcode
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_VERSION = 'v19-torch-focus';

let containerId = 0;

const BarcodeScanner = ({ onScan, onError, isActive = true }) => {
  const [id] = useState(() => `qr-reader-${++containerId}`);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);

  const [status, setStatus] = useState('init');
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const lastScanTimeRef = useRef(0);
  const lastScannedRef = useRef(null); // dedup bez re-tworzenia handleDecode
  // Zoom kamery (o ile iPhone/przegladarka wspiera) — reczna regulacja suwakiem
  const trackRef = useRef(null);
  const desiredZoomRef = useRef(null); // pamieta zoom miedzy restartami kamery
  const [zoomCaps, setZoomCaps] = useState(null); // { min, max, step }
  const [zoomValue, setZoomValue] = useState(1);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const applyZoom = useCallback((z) => {
    desiredZoomRef.current = z; // zapamietaj wybor uzytkownika
    setZoomValue(z);
    const track = trackRef.current;
    if (track && track.applyConstraints) {
      track.applyConstraints({ advanced: [{ zoom: z }] }).catch(() => {});
    }
  }, []);

  const toggleTorch = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.applyConstraints) return;
    const next = !torchOn;
    track.applyConstraints({ advanced: [{ torch: next }] })
      .then(() => setTorchOn(next))
      .catch(() => {});
  }, [torchOn]);

  // Przeostrzenie na dotkniecie podgladu — nudge autofokusu (best-effort).
  // iOS czesto ignoruje focusMode, ale ponowne wymuszenie 'continuous' czasem
  // zmusza aparat do ponownego zlapania ostrosci.
  const refocus = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.applyConstraints) return;
    const caps = track.getCapabilities ? track.getCapabilities() : {};
    const advanced = [];
    if (caps.focusMode && Array.isArray(caps.focusMode)) {
      if (caps.focusMode.includes('single-shot')) advanced.push({ focusMode: 'single-shot' });
      else if (caps.focusMode.includes('continuous')) advanced.push({ focusMode: 'continuous' });
    }
    if (advanced.length) {
      track.applyConstraints({ advanced })
        .then(() => {
          // wroc do ciaglego po chwili
          if (caps.focusMode && caps.focusMode.includes && caps.focusMode.includes('continuous')) {
            setTimeout(() => track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {}), 400);
          }
        })
        .catch(() => {});
    }
  }, []);

  const playBeep = useCallback(() => {
    try {
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      gain.gain.value = 0.15;
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close(); }, 150);
    } catch (e) {}
  }, []);

  const handleDecode = useCallback((text) => {
    if (!mountedRef.current || !text) return;
    const now = Date.now();
    // Dedup przez ref (nie stan) — dzieki temu handleDecode jest stabilny
    // i nie wymusza re-inicjalizacji streamu kamery przy kazdym skanie.
    if (text === lastScannedRef.current && now - lastScanTimeRef.current < 2000) return;

    lastScanTimeRef.current = now;
    lastScannedRef.current = text;
    setLastScanned(text);
    playBeep();
    onScan(text);

    setTimeout(() => {
      if (mountedRef.current) {
        lastScannedRef.current = null;
        setLastScanned(null);
      }
    }, 2000);
  }, [onScan, playBeep]);

  // Stabilne referencje do callbackow uzywanych przez startScanner,
  // tak aby startScanner NIE byl odtwarzany (a wiec nie restartowal kamery)
  // gdy zmieni sie handleDecode/onError miedzy renderami.
  const handleDecodeRef = useRef(handleDecode);
  const onErrorRef = useRef(onError);
  useEffect(() => { handleDecodeRef.current = handleDecode; }, [handleDecode]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const stopScanner = useCallback(async () => {
    // Zatrzymaj html5-qrcode
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (e) {
        // ignore stop errors
      }
      scannerRef.current = null;
    }
    trackRef.current = null;
    setTorchOn(false); // torch gasnie fizycznie ze streamem
    // Zatrzymaj WSZYSTKIE aktywne streamy kamery (fix czarnego ekranu)
    try {
      const container = document.getElementById(id);
      if (container) {
        const videos = container.querySelectorAll('video');
        videos.forEach(video => {
          if (video.srcObject) {
            video.srcObject.getTracks().forEach(track => track.stop());
            video.srcObject = null;
          }
        });
        container.innerHTML = '';
      }
    } catch (e) {
      // ignore cleanup errors
    }
  }, [id]);

  const startScanner = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      setStatus('starting');
      setErrorMsg(null);

      await stopScanner();

      // Czekaj na DOM
      await new Promise(r => setTimeout(r, 500));
      if (!mountedRef.current) return;

      const container = document.getElementById(id);
      if (!container) return;

      // Wyczyść
      container.innerHTML = '';

      // Konfiguracja dekodowania (drugi arg) — nie wplywa na start kamery.
      // Bez QR_CODE (0) — dekodowanie 2D jest ciezkie, a produkty go nie
      // uzywaja. Zostaja tylko kody kreskowe produktow (1D) -> szybciej.
      const scanConfig = {
        fps: 15,
        disableFlip: false,
        // BEZ qrbox — skanujemy cala klatke (jak w dzialajacym v9). Pasek na
        // iPhone wymuszal trafienie kodem w waski obszar i bylo GORZEJ.
        // Predkosc/odleglosc poprawia ciagly autofokus (nizej) + brak QR.
        formatsToSupport: [
          4, // EAN_13
          5, // EAN_8
          9, // CODE_128
          10, // CODE_39
          12, // ITF
          14, // UPC_A
          15  // UPC_E
        ]
      };
      const onDecode = (text) => {
        if (mountedRef.current) handleDecodeRef.current(text);
      };

      // Nowy skaner
      const scanner = new Html5Qrcode(id, { verbose: false });
      scannerRef.current = scanner;

      // Start: najpierw probujemy ograniczonej rozdzielczosci (1280x720) —
      // iPhone domyslnie daje bardzo wysoka rozdzielczosc, a ZXing dekoduje CALA
      // klatke 15x/s i nie nadaza ("muli"); mniejsza klatka = szybsze dekodowanie.
      // width/height 'ideal' sa best-effort i nie powinny wywalac kamery, ale na
      // wszelki wypadek FALLBACK do minimalnych ustawien, gdyby iPhone odrzucil.
      try {
        // Wysoka rozdzielczosc (1920x1080) — wiecej pikseli na kodzie kreskowym,
        // wiec czyta sie z WIEKSZEJ odleglosci (iPhone/ZXing potrzebuje ostrego
        // i gestego obrazu). Predkosc trzyma qrbox (dekodujemy tylko pasek).
        await scanner.start(
          { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          scanConfig, onDecode, () => {}
        );
      } catch (constraintErr) {
        console.warn('[BarcodeScanner] Start z rozdzielczoscia nieudany, fallback do minimalnych:', constraintErr);
        // Odswiez instancje (po nieudanym starcie moze byc w zlym stanie)
        try { await scanner.stop(); } catch (e) {}
        try { scanner.clear(); } catch (e) {}
        const scanner2 = new Html5Qrcode(id, { verbose: false });
        scannerRef.current = scanner2;
        await scanner2.start(
          { facingMode: "environment" },
          scanConfig, onDecode, () => {}
        );
      }

      // Po starcie: dostraj tor wideo wg REALNYCH mozliwosci kamery
      // (getCapabilities), best-effort — jesli iPhone czegos nie wspiera, po
      // prostu pomijamy, kamera dziala dalej.
      // - focusMode 'continuous' -> ciagle ostrzenie (o ile wspierane).
      // - ZOOM ~2x -> powieksza kod BEZ fizycznego przyblizania. Glowny fix
      //   "trzeba przyblizac": trzymasz produkt z komfortowej odleglosci (gdzie
      //   glowny aparat iPhone ostrzy), a zoom robi kod dostatecznie duzy dla
      //   dekodera. Zapisujemy tor + zakres zoomu do recznej regulacji suwakiem.
      try {
        const cont = document.getElementById(id);
        const video = cont?.querySelector('video');
        const track = video?.srcObject?.getVideoTracks?.()[0];
        if (track) {
          trackRef.current = track;
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          const advanced = [];
          if (caps.focusMode && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
            advanced.push({ focusMode: 'continuous' });
          }
          if (caps.zoom && typeof caps.zoom.max === 'number') {
            const zmin = caps.zoom.min || 1;
            const zmax = caps.zoom.max;
            // Zachowaj zoom wybrany przez uzytkownika miedzy restartami kamery
            // (po skanie kamera stopuje sie na czas dialogu ilosci i startuje
            // ponownie) — nie wracaj do auto-2x, jesli user ustawil inny.
            const zoom = desiredZoomRef.current != null
              ? Math.min(Math.max(desiredZoomRef.current, zmin), zmax)
              : Math.min(2, zmax);
            desiredZoomRef.current = zoom;
            if (zoom > zmin) advanced.push({ zoom });
            if (mountedRef.current) {
              setZoomCaps({ min: zmin, max: zmax, step: caps.zoom.step || 0.1 });
              setZoomValue(zoom);
            }
          }
          if (advanced.length && track.applyConstraints) {
            await track.applyConstraints({ advanced }).catch(() => {});
          }
          // Latarka (torch) — mocno pomaga autofokusowi zlapac ostrosc przy
          // slabym swietle (glowna przyczyna "polowania" AF na iPhone).
          if (mountedRef.current) {
            setTorchSupported(!!(caps.torch));
          }
        }
      } catch (e) { /* dostrajanie best-effort */ }

      if (mountedRef.current) {
        setStatus('running');
      }

    } catch (err) {
      console.error('Scanner error:', err);
      if (mountedRef.current) {
        setStatus('error');
        let msg = err.message || 'Nie można uruchomić kamery';
        if (err.name === 'NotAllowedError') {
          msg = 'Brak dostępu do kamery';
        }
        setErrorMsg(msg);
        onErrorRef.current?.(msg);
      }
    }
  }, [id, stopScanner]);

  // Mount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, [stopScanner]);

  // Active
  useEffect(() => {
    if (isActive) {
      startScanner();
    } else {
      stopScanner();
      setStatus('init');
    }
  }, [isActive, startScanner, stopScanner]);

  // Auto-recovery: sprawdzaj czy stream kamery żyje co 3s
  useEffect(() => {
    if (status !== 'running' || !isActive) return;
    const checkInterval = setInterval(() => {
      if (!mountedRef.current) return;
      try {
        const container = document.getElementById(id);
        const video = container?.querySelector('video');
        if (!video) return;

        // Sprawdź czy stream jest aktywny
        const stream = video.srcObject;
        const tracks = stream?.getTracks() || [];
        const allEnded = tracks.length === 0 || tracks.every(t => t.readyState === 'ended');
        // PELNY restart TYLKO gdy tor kamery faktycznie sie zakonczyl / brak
        // danych. Sama PAUZA to NIE smierc — iOS pauzuje wideo gdy otwiera sie
        // okno ilosci po skanie; wczesniej robilismy tu restart (reset zoomu i
        // "wali restarty po kazdym produkcie"). Teraz tylko wznawiamy play().
        const trackDead = allEnded || video.readyState === 0;

        if (trackDead) {
          console.warn('[BarcodeScanner] Tor kamery zakonczony — restartuje...');
          stopScanner();
          setTimeout(() => { if (mountedRef.current) startScanner(); }, 800);
        } else if (video.paused) {
          // Wznow bez restartu — zoom i stream zostaja nietkniete
          video.play().catch(() => {});
        }
      } catch (e) {
        // ignore
      }
    }, 3000);
    return () => clearInterval(checkInterval);
  }, [status, isActive, id, stopScanner, startScanner]);

  const handleRetry = async () => {
    await stopScanner();
    setErrorMsg(null);
    setStatus('init');
    setLastScanned(null);
    setTimeout(() => startScanner(), 500);
  };

  if (status === 'error') {
    return (
      <div style={{ padding: '1.5rem', backgroundColor: 'var(--warning-light)', borderRadius: '8px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '0.5rem' }}>📷</div>
        <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Problem z kamerą</div>
        <div style={{ fontSize: '12px', marginBottom: '1rem' }}>{errorMsg}</div>
        <button onClick={handleRetry} style={{
          padding: '6px 12px', fontSize: '12px', border: '1px solid var(--warning)', color: 'var(--warning)'
        }}>
          🔄 Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        id={id}
        onClick={refocus}
        title="Dotknij, aby przeostrzyc"
        style={{
          width: '100%',
          minHeight: '280px',
          backgroundColor: '#000',
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer'
        }}
      />

      {/* Latarka — pomaga autofokusowi zlapac ostrosc przy slabym swietle
          (glowna przyczyna slabego fokusu na iPhone). Widoczna gdy wspierana. */}
      {status === 'running' && (torchSupported || zoomCaps) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '8px', padding: '0 4px' }}>
          {torchSupported && (
            <button
              onClick={toggleTorch}
              style={{
                padding: '6px 12px', fontSize: '12px', cursor: 'pointer',
                border: `1px solid ${torchOn ? 'var(--warning)' : 'var(--border)'}`,
                backgroundColor: torchOn ? 'var(--warning-light)' : 'var(--card-bg)',
                color: torchOn ? 'var(--warning)' : 'var(--text)'
              }}
            >
              {torchOn ? '🔦 Wył.' : '🔦 Latarka'}
            </button>
          )}
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Dotknij podglądu, aby przeostrzyć</span>
        </div>
      )}

      {/* Suwak zoomu — pomaga zeskanowac kod z komfortowej odleglosci na iPhone
          (powieksza kod bez fizycznego przyblizania). Widoczny tylko gdy kamera
          wspiera zoom. */}
      {status === 'running' && zoomCaps && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '0 4px' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>🔍</span>
          <button
            onClick={() => applyZoom(Math.max(zoomCaps.min, +(zoomValue - (zoomCaps.step || 0.1) * 2).toFixed(2)))}
            style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', cursor: 'pointer' }}
          >−</button>
          <input
            type="range"
            min={zoomCaps.min}
            max={zoomCaps.max}
            step={zoomCaps.step || 0.1}
            value={zoomValue}
            onChange={(e) => applyZoom(parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <button
            onClick={() => applyZoom(Math.min(zoomCaps.max, +(zoomValue + (zoomCaps.step || 0.1) * 2).toFixed(2)))}
            style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', cursor: 'pointer' }}
          >+</button>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '34px', textAlign: 'right' }}>{zoomValue.toFixed(1)}×</span>
        </div>
      )}

      <div style={{
        textAlign: 'center', marginTop: '0.75rem', fontSize: '12px',
        color: status === 'running' ? 'var(--success)' : 'var(--text-muted)'
      }}>
        {status === 'running' ? (
          <span>
            <span style={{
              display: 'inline-block', width: '8px', height: '8px',
              backgroundColor: 'var(--success)', borderRadius: '50%', marginRight: '6px',
              animation: 'pulse 1s infinite'
            }}/>
            Skieruj kamerę na kod kreskowy
          </span>
        ) : (
          'Uruchamianie kamery...'
        )}
        <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '4px' }}>
          {SCANNER_VERSION}
          {status === 'running' && (
            <button
              onClick={handleRetry}
              style={{
                marginLeft: '8px', padding: '4px 10px', fontSize: '11px',
                border: '1px solid var(--text-muted)',
                color: 'var(--text-muted)', backgroundColor: 'transparent', cursor: 'pointer'
              }}
            >
              🔄 Restart
            </button>
          )}
        </div>
      </div>

      {lastScanned && (
        <div style={{
          textAlign: 'center', marginTop: '0.5rem', padding: '0.75rem',
          backgroundColor: 'var(--success-light)', color: 'var(--success)', borderRadius: '8px',
          fontWeight: '600', border: '2px solid var(--success)'
        }}>
          ✓ {lastScanned}
        </div>
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
};

export default BarcodeScanner;
